import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Plus, Search, Edit, Trash2, Package } from 'lucide-react';
import { labTestService, LabTest } from '../../lib/services.lab';
import { useStore } from '../../lib/store';
import { can, getUserRoleFromDB } from '../../lib/rbac';
import toast from 'react-hot-toast';

export default function TestCatalog() {
  const { formatCurrency } = useCurrency();
  const { user } = useStore();
  const [tests, setTests] = useState<LabTest[]>([]);
  const [filteredTests, setFilteredTests] = useState<LabTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTest, setEditingTest] = useState<LabTest | null>(null);
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    checkPermissions();
    loadTests();
  }, []);

  useEffect(() => {
    filterTests();
  }, [tests, search, departmentFilter]);

  const checkPermissions = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setCanManage(role === 'admin');
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadTests = async () => {
    try {
      const testsData = await labTestService.getAll(false);
      setTests(testsData);
    } catch (error) {
      console.error('Failed to load tests:', error);
      toast.error('Failed to load test catalog');
    } finally {
      setLoading(false);
    }
  };

  const filterTests = () => {
    let filtered = tests;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(test =>
        test.name.toLowerCase().includes(searchLower) ||
        test.code.toLowerCase().includes(searchLower)
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter(test => test.department === departmentFilter);
    }

    setFilteredTests(filtered);
  };

  const departments = Array.from(new Set(tests.map(t => t.department)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Test Catalog</h1>
          <p className="text-sm text-neutral-600">Manage laboratory test catalog</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingTest(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Test
          </button>
        )}
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Tests</p>
              <p className="text-2xl font-bold text-neutral-900">{tests.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Active</p>
              <p className="text-2xl font-bold text-neutral-900">
                {tests.filter(t => t.active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Panels</p>
              <p className="text-2xl font-bold text-neutral-900">
                {tests.filter(t => t.is_panel).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Departments</p>
              <p className="text-2xl font-bold text-neutral-900">{departments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tests Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">TAT (hrs)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    No tests found
                  </td>
                </tr>
              ) : (
                filteredTests.map((test) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{test.code}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{test.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.department}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.sample_type}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{test.tat_hours}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">${test.price.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        test.is_panel ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {test.is_panel ? 'Panel' : 'Single'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        test.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {test.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
